<template>
  <div class="settings-block" :key="route.path">
    <div class="settings-block__title">
      <div class="settings-block__title-text">{{ t('Users Management') }}</div>
      <div class="settings-block__title-buttons">
        <a
          :href="`https://docs.bemob.com/${locale}/multi-user-access`"
          class="panel-title-link"
          target="_blank"
        >
          <ui-tooltip-icon
            preset="help"
            :text="t('Find out more about user management in our guides')"
          />
          <span class="read-guide ml-6">
            {{ t('Read Guide') }}
          </span>
        </a>

        <ui-button
          class="mr-0"
          icon="fas fa-plus"
          :text="t('Create Role')"
          button-style="accent"
          @click="createMember()"
        />
      </div>
    </div>

    <div class="settings-container">
      <div
        class="mb-12 w80"
        v-html="
          t(
            'Multi-user access enables all the team members to work under the same account. As the account owner, you can set individual permits for viewing and editing the data to each member. More details concerning roles and permissions find in {0}.',
            [
              `<a href='https://docs.bemob.com/${locale}/multi-user-access' target='_blank'>BeMob Help Desk</a>`,
            ],
          )
        "
      ></div>

      <ui-copy-input :label="t('Login URL for your users')" :value="memberLink" />

      <ui-table
        class="mt-16 main-table"
        :loading="membersLoading"
        :headers="headers"
        :items="tableRows"
        :virtualize="false"
        :rows-per-page-count="10"
        :use-search="false"
      >
        <template #item.fullName="{ item }">
          <div class="main-table__name">
            <div class="main-table__name-text">
              {{ item.fullName }}
            </div>
            <ui-icon
              v-if="item.status === 'blocked'"
              class="ml-4"
              size="12"
              :color="$colors.red"
              icon="fas fa-lock"
            />
          </div>
        </template>
        <template #item.email="{ item }">{{ item.email }} </template>
        <template #item.role="{ item }">{{ item.role }} </template>
        <template #item.workspaces="{ item }">
          <span
            v-if="!item.workspaces || (item.workspaces && item.workspaces.length === 0)"
            class="not-assigned"
          >
            {{ t('Not Assigned') }}
          </span>
          <div v-if="item.workspaces?.length > 0" class="onePlus-wrp">
            <div class="onePlus-text">
              {{ item.workspaces[0].name }}
            </div>
            <v-tooltip
              v-if="item.workspaces.length > 1"
              :color="$colors.white"
              content-class="onePlus-tooltip"
              class="ml-8"
              location="top"
            >
              <template #activator="{ props }">
                <div class="onePlus-text onePlus-text--count ml-8" v-bind="props">
                  {{ `+${item.workspaces.length - 1}` }}
                </div>
              </template>
              <div v-for="workspace in item.workspaces.slice(1)" class="onePlus-text">
                {{ workspace.name }}
              </div>
            </v-tooltip>
          </div>
        </template>
        <template #item.created="{ item }">
          {{ utcToLocal(item.created).format('DD MMM YYYY HH:mm') }}
        </template>
        <template #item.actions="{ item }">
          <div class="actions">
            <button
              class="action-button"
              :disabled="item.status === 'blocked'"
              :class="{ 'action-button--disabled': item.status === 'blocked' }"
              type="button"
              @click="showConfirmImpersonate(item)"
            >
              <ui-icon
                :color="item.status === 'blocked' ? $colors.gray40 : $colors.green"
                class="mr-4"
                icon="fas fa-sign-in"
              />
              {{ t('Log In') }}
            </button>

            <button class="action-button" type="button" @click="createMember(item.id)">
              <ui-icon :color="$colors.green" class="mr-4" icon="fas fa-pen" />
              {{ t('Edit') }}
            </button>

            <button
              class="action-button"
              type="button"
              @click="deleteUser(item.id)"
              :style="{ color: 'var(--color-red)' }"
            >
              <ui-icon :color="$colors.red" class="mr-4" icon="fas fa-trash-alt" />
              {{ t('Delete') }}
            </button>
          </div></template
        >
      </ui-table>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import moment from 'moment';
import { deleteMemberApiRequest } from '@/services/api/user-api';
import { refreshUserProfileFromApi } from '@/services/api/api-services/load-profile';
import { impersonateMember } from '@/services/api/api-services/impersonate-member';
import type { UserWorkspace } from '@/services/models';
import { useColors, useI18n, useMixpanel } from '@/composables';
import { useStore } from '@/store';
import $emitter from '@/services/emitter';

type MemberRow = {
  id: string;
  email: string;
  role: string;
  fullName: string;
  created: string;
  workspaces: { name: string }[] | null;
  status: string;
};

const props = withDefaults(
  defineProps<{
    privateWorkspacesData?: UserWorkspace[];
    privateWorkspacesDataNormalized?: Record<string, UserWorkspace>;
    refreshWorkspaces?: () => Promise<void>;
    membersData?: unknown[];
    membersDataNormalized?: Record<string, unknown>;
    membersLoading?: boolean;
    refreshMembers?: () => Promise<void>;
  }>(),
  {
    privateWorkspacesData: () => [],
    privateWorkspacesDataNormalized: () => ({}),
    membersData: () => [],
    membersDataNormalized: () => ({}),
    membersLoading: false,
  },
);

const route = useRoute();
const router = useRouter();
const { t, locale } = useI18n();
const $colors = useColors();
const store = useStore();
const { trackMixpanel } = useMixpanel();

const utcToLocal = (date: string) => moment(moment.utc(date).toDate()).local();

const memberLink = computed(() => {
  const port = location.port ? `:${location.port}` : '';
  const routeParams = {
    name: 'login.member',
    query: {
      accountId: store.user.id,
    },
  };
  const path = router.resolve(routeParams).href.replace(/^\//, '');

  return `${location.protocol}//${location.hostname}${port}/${path}`;
});

const tableRows = computed(() => {
  if (!props.membersData) return [];
  return props.membersData.map((member: Record<string, unknown>) => ({
    email: member.login,
    role: member.role,
    fullName: member.fullName,
    created: member.created,
    workspaces: member.workspaces || null,
    actions: '',
    canCreate: member.canCreate,
    id: member.id,
    accessToExistingItems: member.accessToExistingItems,
    status: member.status,
    loginUrl: member.loginUrl,
  })) as MemberRow[];
});

const headers = computed(() => [
  {
    title: t('Name'),
    value: 'fullName',
    sortable: false,
  },
  {
    title: t('Email'),
    value: 'email',
    sortable: false,
  },
  {
    title: t('Role'),
    value: 'role',
    sortable: false,
  },
  {
    title: t('Private Workspaces'),
    value: 'workspaces',
    sortable: false,
  },
  {
    title: t('Created at'),
    value: 'created',
    sortable: false,
  },
  {
    value: 'actions',
    sortable: false,
  },
]);

const deleteUser = (id: string) => {
  $emitter.emit('open-dialog', {
    name: 'ConfirmDialog',
    data: {
      message: t('All selected items will be deleted permanently if you press OK.'),
      onConfirm: () => {
        void deleteMemberApiRequest(id)
          .then(async () => {
            if (props.refreshWorkspaces) {
              await props.refreshWorkspaces();
            }
            await refreshUserProfileFromApi();
            if (props.refreshMembers) {
              await props.refreshMembers();
            }
          })
          .finally(() => $emitter.emit('toast-success', t('User successfully deleted.')));
      },
    },
  });
};

const createMember = (id?: string) => {
  const user = (id ? props.membersDataNormalized[id] : null) || null;
  $emitter.emit('open-dialog', {
    name: 'CreateMember',
    data: {
      user,
      privateWorkspacesData: props.privateWorkspacesData,
      privateWorkspacesDataNormalized: props.privateWorkspacesDataNormalized,
      refreshWorkspaces: props.refreshWorkspaces,
      refreshMembers: props.refreshMembers,
    },
  });
};

const showConfirmImpersonate = (member: { id: string; email: string }) => {
  $emitter.emit('open-dialog', {
    name: 'ConfirmDialog',
    data: {
      message: t(
        'When logging in as another user or admin, you will be provided with the read-only access to settings of this member.<br> No editing in the member’s account allowed.<br> You can go back to your account by clicking <b>Return back</b> at the top of the page.',
      ),
      confirmButtonText: t('Log In As Member'),
      confirmButtonIcon: 'fas fa-sign-in',
      onConfirm: () => {
        impersonateMember(member);

        trackMixpanel('settings_impersonate', {
          source: 'table',
          owner_email: store.user.email,
          member_email: member.email,
        });
      },
    },
  });
};
</script>

<style lang="scss" scoped>
@import '@/styles/pages/settings/multi-user-access';

.error-block {
  width: 100%;
  background: var(--color-red);
  color: var(--color-white);
  padding: 16px;
}

.main-table {
  :deep(.ui-table__inner) {
    table-layout: fixed;
  }

  :deep(.table-header > .table-cell),
  :deep(.table-body .table-row > .table-cell) {
    text-overflow: ellipsis;
    overflow: hidden;
    text-align: left;
    padding-right: 5px !important;

    &:nth-child(1) {
      width: 180px !important;
      white-space: nowrap;
    }
    &:nth-child(2) {
      width: 180px !important;
    }
    &:nth-child(3) {
      width: 80px !important;
    }
    &:nth-child(4) {
      width: 150px !important;
    }
    &:nth-child(5) {
      width: 130px !important;
    }
    &:last-child {
      width: 185px !important;
    }
    &:last-child:lang(es) {
      width: 205px !important;
    }
    &:last-child:lang(ru) {
      width: 235px !important;
    }
  }
}

.actions {
  display: flex;
  justify-content: flex-end;
}

.action-button {
  display: flex;
  align-items: center;
  color: var(--color-green);
  margin-left: 16px;
  white-space: nowrap;
}

.action-button--disabled {
  color: var(--color-gray40);
  cursor: default;
}

.main-table__name {
  display: flex;
}

.main-table__name-text {
  text-overflow: ellipsis;
  overflow: hidden;
}

.not-assigned {
  color: var(--color-gray40);
}
</style>
